import { Checkbox, AccordionDetails, Grid, FormControlLabel } from '@mui/material';
import { DatePicker } from '@mui/lab';
import { CirculationAreaForm } from '..';
import { useEffect, useMemo } from 'react';
import { FormikProps } from 'formik';
import TypeReason from './type-reason';
import CivicState from './civic-state';
import Municipality from './municipality';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { Civic } from 'shell/models/civic';
import { DateTime } from 'luxon';
import PmiTextField from 'shell/components/shared/text-field';
import UrbanArea from './urban-area';
import CivicType from './civic-type';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { FitersCadastralVM } from 'system/pages/toponymy/circulation-area/list/types';
import LocalAreaToponymy from 'municipal-messenger/components/form/autocomplete-local-area';
import AutocompleteMessoList from './messo-list';

interface FormCivicProps {
  disabled?: boolean;
  circulationAreaForm: FormikProps<CirculationAreaForm>;
  circulationArea?: number;
  setOpen: (open: boolean) => void;
}

const CivicForm = ({
  circulationAreaForm,
  disabled,
  circulationArea,
  setOpen,
}: FormCivicProps): React.ReactElement => {
  const filterTable = useFilterTable<FitersCadastralVM>();

  const queryParams = useMemo(() => {
    let queryParamsInternal = '?';

    if (
      !circulationAreaForm.values.streetNumber?.esponente &&
      !filterTable.values?.circulationAreaCadastral
    ) {
      return '';
    }

    if (circulationAreaForm.values.streetNumber?.esponente) {
      queryParamsInternal = `${queryParamsInternal}esponente=${
        circulationAreaForm.values.streetNumber?.esponente
      }${
        circulationAreaForm.values.streetNumber?.dataIstituzione
          ? `&dataIstituzione=${circulationAreaForm.values.streetNumber?.dataIstituzione}`
          : ''
      }`;
    }

    if (filterTable.values?.circulationAreaCadastral) {
      queryParamsInternal = `${queryParamsInternal}${
        circulationAreaForm.values.streetNumber?.esponente ? '&' : ''
      }foglio=${filterTable.values?.circulationAreaCadastral?.sheet}&particella=${
        filterTable.values?.circulationAreaCadastral?.particle
      }&subalterno=${filterTable.values?.circulationAreaCadastral?.subordinate}`;
    }
    return queryParamsInternal;
  }, [filterTable.values, circulationAreaForm.values.streetNumber]);

  const { get: getCivicData } = useHttpFetch<HttpResponse<Civic>>(
    `${__TOPONYMY_API_URL__}/areaCircolazione/${circulationArea}/civico-esteso/${circulationAreaForm.values?.streetNumber?.id}${queryParams}`
  );

  useEffect(() => {
    getCivicData()
      .then((response) => {
        const serviceResponse = response as HttpResponse<Civic>;
        const { payload: civic } = serviceResponse;
        circulationAreaForm.setValues({
          ...circulationAreaForm.values,
          electoralSection: civic.sezioneElettorale?.toString(),
          endReason: civic.motivoCessazione,
          key: civic.key.esponente,
          dateKey: civic.key?.dataIstituzione ? DateTime.fromISO(civic.key?.dataIstituzione) : null,
          civicState: civic.statoCivico,
          municipality: civic.municipio?.toLocaleString('it', {
            minimumIntegerDigits: 2,
          }),
          entered: civic.provvisorio === 'S',
          estabilishmentReason: civic.motivo,
          temporary: civic.provvisorio === '*',
          streetEnd: !!civic.motivoCessazione?.trim(),
          cap: civic.cap.toString(),
          civic: civic.ambitoTerritoriale,
          estabilishmentDateCivic: civic.key.dataIstituzione
            ? DateTime.fromISO(civic.key.dataIstituzione)
            : null,
          endDateCivic: civic.dataCessazione ? DateTime.fromISO(civic.dataCessazione) : null,
          urbanArea: civic?.zonaUrbanistica || '',
          territorialArea: civic?.ambitoTerritoriale || '',
          civicType: civic?.tipologiaAbitazione || '',
          censusSection: civic?.sezCensim?.toString() || '',
          localIdentity: civic?.dsb?.toString() || '',
          municipalMessenger: civic?.messo ? { id: '', value: civic?.messo } : null,
          addressSection: civic?.indirizzoSezione || '',
          cadastralList: civic?.datiCatastali?.map((item, index) => {
            let lock = false;

            const dateStart = item?.dataInizioBlocco
              ? DateTime.fromFormat(item?.dataInizioBlocco, 'yyyyMMdd')
              : null;

            const dateEnd = item?.dataFineBlocco
              ? DateTime.fromFormat(item?.dataFineBlocco, 'yyyyMMdd')
              : null;

            const endDateFixed = DateTime.fromFormat('31122999', 'ddMMyyyy');

            if (!dateStart || (dateEnd && dateEnd.isValid && !dateEnd.equals(endDateFixed))) {
              lock = true;
            }

            if (!dateEnd || (dateEnd && dateEnd.isValid && dateEnd.equals(endDateFixed))) {
              lock = false;
            }

            if (!dateStart && !dateEnd) {
              lock = true;
            }
            return {
              id: index,
              sheet: item?.foglio?.trim() || '',
              particle: item?.particella?.trim() || '',
              section: item?.sezione?.trim() || '',
              subordinate: item?.subalterno || '',
              catCadastral: item?.categoriaCatastale || '',
              startBlockDate: item?.dataInizioBlocco ? dateStart?.toFormat('dd/MM/yyyy') : '',
              endBlockDate: item?.dataFineBlocco ? dateEnd?.toFormat('dd/MM/yyyy') : '',
              residentsNumber: item?.numeroAbitanti,
              lock,
              el: item,
            };
          }),
        });
        setOpen(true);
      })
      .catch(() => {
        circulationAreaForm.setValues({
          ...circulationAreaForm.values,
          electoralSection: '',
          endReason: '',
          key: '',
          dateKey: null,
          civicState: '',
          municipality: '',
          entered: false,
          estabilishmentReason: '',
          temporary: false,
          streetEnd: false,
          cap: '',
          streetNumber: undefined,
          civic: '',
          civicType: '',
          urbanArea: '',
          estabilishmentDateCivic: undefined,
          endDateCivic: undefined,
          territorialArea: '',
          municipalMessenger: null,
          censusSection: '',
          localIdentity: '',
          cadastralList: [],
        });
      });
  }, [circulationAreaForm.values?.streetNumber, queryParams]);

  useEffect(() => {
    if (!circulationAreaForm.values.endDateCivic && !circulationAreaForm.values.endReason) {
      circulationAreaForm.setFieldValue('streetEnd', false);
    } else {
      circulationAreaForm.setFieldValue('streetEnd', true);
    }
  }, [circulationAreaForm.values.endDateCivic, circulationAreaForm.values.endReason]);

  return (
    <AccordionDetails>
      <Grid container spacing={2} alignItems="center">
        <Grid container item xs={12} spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <PmiTextField
              label="CAP"
              id="cap"
              name="cap"
              fullWidth
              type="text"
              variant="outlined"
              value={circulationAreaForm.values.cap || ''}
              disabled={disabled}
              onChange={circulationAreaForm.handleChange}
              error={circulationAreaForm.touched.cap && Boolean(circulationAreaForm.errors.cap)}
              helperText={circulationAreaForm.errors.cap}
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  id="streetEnd"
                  name="streetEnd"
                  disabled
                  checked={circulationAreaForm.values.streetEnd || false}
                  onChange={(event) => {
                    const form: CirculationAreaForm = {
                      ...circulationAreaForm.values,
                      streetEnd: !!event.target.checked,
                    };
                    circulationAreaForm.setValues(form);
                  }}
                  inputProps={{ 'aria-label': 'cessato' }}
                />
              }
              label="Cessato"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  id="temporary"
                  name="temporary"
                  disabled
                  checked={circulationAreaForm.values.temporary || false}
                  onChange={(event) => {
                    const form: CirculationAreaForm = {
                      ...circulationAreaForm.values,
                      temporary: !!event.target.checked,
                    };
                    circulationAreaForm.setValues(form);
                  }}
                  inputProps={{ 'aria-label': 'provvisorio' }}
                />
              }
              label="Provvisorio"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  id="entered"
                  name="entered"
                  disabled
                  checked={circulationAreaForm.values.entered || false}
                  onChange={(event) => {
                    const form: CirculationAreaForm = {
                      ...circulationAreaForm.values,
                      entered: !!event.target.checked,
                    };
                    circulationAreaForm.setValues(form);
                  }}
                  inputProps={{ 'aria-label': 'seconda entrata' }}
                />
              }
              label="II Entrata"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} item xs={12}>
          <Grid item>
            <DatePicker
              label="Data Istituzione"
              value={circulationAreaForm.values.estabilishmentDateCivic || null}
              disabled={disabled}
              onChange={(value) =>
                circulationAreaForm.setFieldValue('estabilishmentDateCivic', value)
              }
              renderInput={(params) => (
                <PmiTextField
                  id="estabilishmentDateCivic"
                  name="estabilishmentDateCivic"
                  {...params}
                  error={false}
                  helperText=""
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TypeReason
              id="estabilishmentReason"
              name="estabilishmentReason"
              label="Motivo Istituzione"
              disabled={disabled}
              value={circulationAreaForm.values.estabilishmentReason || ''}
              onChange={circulationAreaForm.handleChange}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} item xs={12}>
          <Grid item>
            <DatePicker
              label="Data Cessazione"
              value={circulationAreaForm.values.endDateCivic || null}
              disabled={disabled}
              onChange={(value) => circulationAreaForm.setFieldValue('endDateCivic', value)}
              renderInput={(params) => (
                <PmiTextField
                  id="endDateCivic"
                  name="endDateCivic"
                  {...params}
                  error={false}
                  helperText=""
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TypeReason
              id="endReason"
              name="endReason"
              label="Motivo Cessazione"
              disabled={disabled}
              value={circulationAreaForm.values.endReason || ''}
              onChange={circulationAreaForm.handleChange}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} md={3}>
          <CivicState
            disabled={disabled}
            value={circulationAreaForm.values.civicState || ''}
            onChange={circulationAreaForm.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <UrbanArea
            disabled={disabled}
            value={circulationAreaForm.values.urbanArea || ''}
            onChange={circulationAreaForm.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CivicType
            disabled={disabled}
            value={circulationAreaForm.values.civicType || ''}
            onChange={circulationAreaForm.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12} md={6}>
            <Municipality
              disabled={disabled}
              value={circulationAreaForm.values.municipality || ''}
              onChange={circulationAreaForm.handleChange}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} md={3}>
          <PmiTextField
            label="Sezione Elettorale"
            id="electoralSection"
            name="electoralSection"
            type="text"
            fullWidth
            variant="outlined"
            value={circulationAreaForm.values.electoralSection || ''}
            disabled={disabled}
            onChange={circulationAreaForm.handleChange}
            error={
              circulationAreaForm.touched.electoralSection &&
              Boolean(circulationAreaForm.errors.electoralSection)
            }
            helperText={circulationAreaForm.errors.electoralSection}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <PmiTextField
            fullWidth
            label="Indirizzo Sezione"
            id="addressSection"
            name="addressSection"
            type="text"
            variant="outlined"
            value={circulationAreaForm.values.addressSection || ''}
            disabled={disabled}
            onChange={circulationAreaForm.handleChange}
            error={
              circulationAreaForm.touched.addressSection &&
              Boolean(circulationAreaForm.errors.addressSection)
            }
            helperText={circulationAreaForm.errors.addressSection}
          />
        </Grid>
        <Grid item xs={3}>
          <LocalAreaToponymy
            value={circulationAreaForm.values.territorialArea || ''}
            disabled={disabled}
            onChange={(value) => circulationAreaForm.setFieldValue('territorialArea', value)}
          />
        </Grid>
        <Grid item xs={8}>
          <AutocompleteMessoList
            label="Messo"
            id="municipalMessenger"
            value={circulationAreaForm.values.municipalMessenger || null}
            disabled
            ambitoTerritoriale={circulationAreaForm.values.territorialArea || ''}
            onChange={(value) => circulationAreaForm.setFieldValue('municipalMessenger', value)}
            error={
              circulationAreaForm.touched.municipalMessenger &&
              Boolean(circulationAreaForm.errors.municipalMessenger)
            }
            helperText={circulationAreaForm.errors.municipalMessenger}
          />
        </Grid>
        <Grid item xs={3}>
          <PmiTextField
            label="Sezione censimento"
            id="censusSection"
            name="censusSection"
            type="text"
            fullWidth
            variant="outlined"
            value={circulationAreaForm.values.censusSection || ''}
            disabled={disabled}
            onChange={circulationAreaForm.handleChange}
            error={
              circulationAreaForm.touched.censusSection &&
              Boolean(circulationAreaForm.errors.censusSection)
            }
            helperText={circulationAreaForm.errors.censusSection}
          />
        </Grid>
        <Grid item xs={3}>
          <PmiTextField
            label="Nucleo Identità Locale"
            id="localIdentity"
            name="localIdentity"
            type="text"
            fullWidth
            variant="outlined"
            value={circulationAreaForm.values.localIdentity || ''}
            disabled={disabled}
            onChange={circulationAreaForm.handleChange}
            error={
              circulationAreaForm.touched.localIdentity &&
              Boolean(circulationAreaForm.errors.localIdentity)
            }
            helperText={circulationAreaForm.errors.localIdentity}
          />
        </Grid>
      </Grid>
    </AccordionDetails>
  );
};

CivicForm.displayName = 'Civic Form';

export default CivicForm;
